// segmentScanText — THE single entry point for turning raw product
// text (OFF productName, OCR, Haiku output) into structured identity:
//
//   { canonicalId, name, brand, variant, claims, residual }
//
// WHY THIS EXISTS (the "fix it once and for all" for flavor/variant
// leakage): the two older tools each have a structural hole —
//
//   • stripFlavors.js is a hand-curated lexicon. Flavors are an OPEN
//     set ("Zesty Ranch", "Sweet Heat", next month's launch…), so
//     the lexicon is permanently behind; every miss leaks marketing
//     copy into a name or drops the variant on the floor.
//   • resolveBrandAndVariant.js only binds a variant when the brand
//     catalog already lists it. When the catalog misses, the variant
//     phrase SHREDS into single-word claims ("Wild Cherry" → "Wild"
//     + "Cherry") or vanishes.
//
// The inversion that closes both holes: identity vocabulary is a
// CLOSED set — the canonical registry (names, slugs, searchAliases).
// So instead of enumerating what IS a flavor, we subtract what is
// definitely NOT one (brand tokens + canonical identity tokens +
// packaging/size noise) and treat the longest contiguous leftover
// phrase as the variant. The registry grows → segmentation improves,
// with zero lexicon curation.
//
// Tier order per scan (most precise wins):
//   1. Catalog segmentation (resolveBrandAndVariant) — brand catalog
//      knows this exact variant ("Pepsi" → "Wild Cherry").
//   2. Lexicon strip (stripFlavors) — claims harvest + name cleanup;
//      still valuable because its multi-word tokens carry curated
//      capitalization ("Honey BBQ", "Cookies & Cream").
//   3. Registry anchor (resolveCanonicalWithClaims) — binds the
//      canonical when the caller didn't, and demotes lower-tier
//      identity matches to claims ("Cheese Frank" → frank + Cheese).
//   4. Phrase fallback (NEW) — canonical bound but variant still
//      null: subtract brand/identity/noise tokens, keep the longest
//      contiguous leftover run (1–4 tokens, original order) as the
//      variant. "Doritos Zesty Ranch Tortilla Chips" → brand Doritos,
//      canonical tortilla_chips, variant "Zesty Ranch" — no lexicon
//      entry required.
//
// NAME RULE (CLAUDE.md identity hierarchy): when a canonical is
// bound, `name` is ALWAYS the canonical's display name. Free text
// only survives into `name` when nothing binds (red tier), and even
// then it's the lexicon-cleaned residual, never raw marketing copy.
//
// Regression suite: scripts/test_flavor_variant.mjs — run
//   node scripts/test_flavor_variant.mjs
// before touching ANY of the four files in this pipeline.

// Explicit .js extensions ON PURPOSE — this module is imported by the
// node-run regression suite (scripts/test_flavor_variant.mjs) as well
// as the webpack bundle, and node ESM requires full specifiers.
import { findIngredient, resolveCanonicalWithClaims, hubForIngredient, findHub } from "../data/ingredients.js";
import { stripFlavors } from "./stripFlavors.js";
import {
  resolveBrandAndVariant,
  tokenizeProductText,
  PRODUCT_STOP_WORDS,
} from "./resolveBrandAndVariant.js";

// Singular/plural fold so "chips" matches identity token "chip" and
// vice versa. Tiny on purpose — full pluralization lives in
// effectiveRecipe's pluralVariants; identity tokens here are short
// registry words where trailing-s covers the real cases.
function foldForms(token) {
  const t = String(token || "").toLowerCase();
  const out = [t];
  if (t.endsWith("s") && t.length > 3) out.push(t.slice(0, -1));
  else out.push(`${t}s`);
  return out;
}

function addIdentityTokens(set, text) {
  if (!text) return;
  for (const tok of tokenizeProductText(String(text).replace(/_/g, " "))) {
    if (tok.length < 2) continue;
    for (const form of foldForms(tok)) set.add(form);
  }
}

// Identity vocabulary for a bound canonical: slug words + display
// name + shortName + searchAliases. Anything in this set is the
// product's IDENTITY and can never be part of its variant.
function identityTokensFor(canonical) {
  const set = new Set();
  if (!canonical) return set;
  addIdentityTokens(set, canonical.id);
  addIdentityTokens(set, canonical.name);
  addIdentityTokens(set, canonical.shortName);
  for (const alias of canonical.searchAliases || []) addIdentityTokens(set, alias);
  return set;
}

// Tokens whose title-case form reads as an OCR error. Mirrors the
// spirit of stripFlavors' CAPITALIZATION_OVERRIDES at the single-
// token level ("Honey Bbq" → "Honey BBQ").
const ALLCAPS_TOKENS = new Set(["bbq", "bbq's", "ipa", "bha", "msg"]);

function titleCasePhrase(phrase) {
  return phrase
    .split(/\s+/)
    .map(w => {
      if (!w) return "";
      if (ALLCAPS_TOKENS.has(w.toLowerCase())) return w.toUpperCase();
      return w[0].toUpperCase() + w.slice(1);
    })
    .join(" ");
}

// Hub-chain vocabulary for a bound canonical — used to drop claims
// that just restate the family ("Cheese" on a cheddar row: cheddar's
// hub chain already says cheese; the chip is noise). Walks
// canonical → hub → parentHub …, bounded for safety.
function hubChainTokensFor(canonical) {
  const set = new Set();
  if (!canonical) return set;
  try {
    let hub = hubForIngredient(canonical.id);
    for (let i = 0; hub && i < 6; i += 1) {
      addIdentityTokens(set, hub.id);
      addIdentityTokens(set, hub.name);
      hub = hub.parentHub ? findHub(hub.parentHub) : null;
    }
  } catch {
    // hub helpers can throw on malformed chains; the filter is
    // best-effort noise reduction, never load-bearing.
  }
  return set;
}

function isNoiseToken(t) {
  if (!t) return true;
  if (PRODUCT_STOP_WORDS.has(t)) return true;
  if (/^\d/.test(t)) return true;            // numbers + "12oz"
  if (t.length < 2) return true;
  return false;
}

// The phrase-fallback pass. Returns { variant, leftovers } where
// variant is the longest contiguous run (1–4 tokens) of non-brand,
// non-identity, non-noise tokens in ORIGINAL order, and leftovers
// are the remaining stray tokens (→ claims). Runs of 5+ tokens are
// whole marketing sentences, not variants — those demote entirely
// to leftovers rather than minting a junk variant.
function deriveVariantPhrase(text, { brand = null, canonical = null } = {}) {
  const tokens = tokenizeProductText(text);
  if (tokens.length === 0) return { variant: null, leftovers: [] };
  const identity = identityTokensFor(canonical);
  const brandTokens = new Set(tokenizeProductText(brand || ""));

  const keep = tokens.map(t =>
    !isNoiseToken(t)
    && !brandTokens.has(t)
    && !foldForms(t).some(f => identity.has(f)));

  // Longest contiguous run of kept tokens; first run wins ties so
  // "Zesty Ranch … Party Size" prefers the leading phrase.
  let best = null;
  let cur = null;
  for (let i = 0; i < tokens.length; i++) {
    if (keep[i]) {
      if (!cur) cur = { start: i, end: i };
      else cur.end = i;
    } else if (cur) {
      if (!best || (cur.end - cur.start) > (best.end - best.start)) best = cur;
      cur = null;
    }
  }
  if (cur && (!best || (cur.end - cur.start) > (best.end - best.start))) best = cur;
  if (!best) return { variant: null, leftovers: [] };

  const runTokens = tokens.slice(best.start, best.end + 1);
  const leftovers = tokens.filter((t, i) =>
    keep[i] && (i < best.start || i > best.end));

  if (runTokens.length > 4) {
    // Marketing-sentence guard — too long to be a product line.
    return { variant: null, leftovers: [...runTokens, ...leftovers] };
  }
  return {
    variant: titleCasePhrase(runTokens.join(" ")),
    leftovers,
  };
}

/**
 * @param {string} text — raw product text (productName, OCR line)
 * @param {object} [opts]
 * @param {string|null} [opts.brand]        — known brand (correction/OFF/Haiku)
 * @param {object[]}    [opts.brandCatalog] — brandsForCanonical(...) output,
 *                                            enables catalog-tier variant match
 * @param {string|null} [opts.canonicalId]  — already-bound canonical; when
 *                                            null we try the registry anchor
 * @returns {{ canonicalId: string|null, name: string|null,
 *             brand: string|null, variant: string|null,
 *             claims: string[], residual: string }}
 */
export function segmentScanText(text, { brand = null, brandCatalog = null, canonicalId = null } = {}) {
  const raw = String(text || "").trim();
  if (!raw) {
    return { canonicalId: canonicalId || null, name: null, brand: brand || null, variant: null, claims: [], residual: "" };
  }

  const claims = [];
  let resolvedBrand = brand || null;
  let variant = null;

  // Tier 1 — catalog segmentation. Precise brand + variant when the
  // canonical's brand catalog knows this product line.
  if (Array.isArray(brandCatalog) && brandCatalog.length > 0) {
    const seg = resolveBrandAndVariant(raw, brandCatalog);
    if (seg?.brand && !resolvedBrand) resolvedBrand = seg.brand;
    if (seg?.variant) variant = seg.variant;
  }

  // Tier 2 — lexicon strip. Harvests curated multi-word flavor claims
  // and produces the cleaned residual we anchor / fall back on.
  const lex = stripFlavors(raw, { brand: resolvedBrand, removeFromName: true });
  claims.push(...lex.claims);
  const residual = lex.remainingName || raw;

  // Tier 3 — registry anchor. Bind the canonical (when the caller
  // didn't) and demote lower-tier identity matches to claims.
  let boundId = canonicalId || null;
  if (!boundId) {
    const resolved = resolveCanonicalWithClaims(residual);
    if (resolved?.canonicalId) {
      boundId = resolved.canonicalId;
      claims.push(...(resolved.claims || []));
    }
  }
  const canonical = boundId ? findIngredient(boundId) : null;

  // Tier 4 — phrase fallback. Only with a bound canonical (identity
  // vocabulary is what makes the subtraction safe).
  if (!variant && canonical) {
    const fb = deriveVariantPhrase(raw, { brand: resolvedBrand, canonical });
    if (fb.variant) variant = fb.variant;
    for (const t of fb.leftovers) claims.push(titleCasePhrase(t));
  }

  // NAME RULE — canonical display name when bound; lexicon-cleaned
  // residual only as the red-tier fallback. Raw marketing copy never
  // passes through.
  const name = canonical?.name || residual || null;

  // Claims hygiene: dedupe case-insensitively; drop claims duplicated
  // by (or contained in) the variant so "Wild Cherry" doesn't ride as
  // both variant AND claim chips; drop claims that just restate the
  // canonical's hub family ("Cheese" on a cheddar row).
  const variantLower = (variant || "").toLowerCase();
  const hubTokens = hubChainTokensFor(canonical);
  const seen = new Set();
  const cleanClaims = [];
  for (const c of claims) {
    const key = String(c || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    if (variantLower && (variantLower === key || variantLower.includes(key))) continue;
    const claimTokens = tokenizeProductText(key);
    const allHubRedundant = claimTokens.length > 0
      && claimTokens.every(t => foldForms(t).some(f => hubTokens.has(f)));
    if (allHubRedundant) continue;
    seen.add(key);
    cleanClaims.push(String(c).trim());
  }

  return {
    canonicalId: boundId,
    name,
    brand: resolvedBrand,
    variant,
    claims: cleanClaims,
    residual,
  };
}
